import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "../lib/api";

const CartCtx = createContext(null);

const KEY = "crave_cart_v1";
const TABLE_KEY = "mnu_table_v1"; // { slug, table_no }
const APPEND_KEY = "mnu_append_v1"; // { slug, order_id }
const SLUG_KEY = "mnu_slug_v1"; // { slug } — so we know which shared cart to poll
const POLL_MS = 4000; // guests who scan the same table see each other's adds within ~4s

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // {item_id, name, price, qty, image_url, notes, added_by_name?}
  const [restaurantId, setRestaurantId] = useState(null);
  const [slug, setSlugState] = useState(null);
  const [tableNo, setTableNoState] = useState(null);
  const [appendOrderId, setAppendOrderIdState] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [sharedActive, setSharedActive] = useState(false);

  // Lock used to suppress local→server writes that happen right after we
  // just pulled the cart from the server (otherwise every poll would ping-pong).
  const suppressPushRef = useRef(false);

  // ---------- hydrate from localStorage ----------
  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        setItems(obj.items || []);
        setRestaurantId(obj.restaurantId || null);
      }
      const t = localStorage.getItem(TABLE_KEY);
      if (t) setTableNoState(JSON.parse(t).table_no || null);
      const a = localStorage.getItem(APPEND_KEY);
      if (a) setAppendOrderIdState(JSON.parse(a).order_id || null);
      const s = localStorage.getItem(SLUG_KEY);
      if (s) setSlugState(JSON.parse(s).slug || null);
    } catch { /* noop */ }
    setLoaded(true);
  }, []);

  // ---------- persist local cart ----------
  useEffect(() => {
    if (!loaded) return;
    localStorage.setItem(KEY, JSON.stringify({ items, restaurantId }));
  }, [items, restaurantId, loaded]);

  // ---------- Shared-cart sync ----------
  // The shared cart kicks in as soon as we have BOTH a slug and a tableNo.
  // Multiple guests scanning the same table's QR all point at the same
  // (restaurant_id, table_no) doc on the backend.
  const pullShared = useCallback(async () => {
    if (!slug || !tableNo) return;
    try {
      const { data } = await api.get(`/customer/restaurant/${slug}/table/${tableNo}/cart`);
      suppressPushRef.current = true;
      setItems(data.items || []);
      if (data.restaurant_id) setRestaurantId(data.restaurant_id);
      setSharedActive(true);
      // release lock next tick
      setTimeout(() => { suppressPushRef.current = false; }, 50);
    } catch { /* noop — fall back to local */ }
  }, [slug, tableNo]);

  useEffect(() => {
    if (!loaded || !slug || !tableNo) { setSharedActive(false); return; }
    pullShared();
    const int = setInterval(pullShared, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") pullShared(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(int); document.removeEventListener("visibilitychange", onVis); };
  }, [loaded, slug, tableNo, pullShared]);

  // Push local items to the shared cart whenever they change, as long as we
  // didn't just pull from the server.
  useEffect(() => {
    if (!loaded) return;
    if (!slug || !tableNo) return;
    if (suppressPushRef.current) return;
    // We intentionally fire-and-forget. Any failure is recovered by the next poll.
    api.put(`/customer/restaurant/${slug}/table/${tableNo}/cart`, { items }).catch(() => {});
  }, [items, slug, tableNo, loaded]);

  // ---------- setters ----------
  const setTableNo = (slugArg, t) => {
    setTableNoState(t || null);
    if (slugArg) setSlug(slugArg);
    try {
      if (t) localStorage.setItem(TABLE_KEY, JSON.stringify({ slug: slugArg, table_no: t }));
      else localStorage.removeItem(TABLE_KEY);
    } catch { /* noop */ }
  };

  const setSlug = (s) => {
    setSlugState(s || null);
    try {
      if (s) localStorage.setItem(SLUG_KEY, JSON.stringify({ slug: s }));
      else localStorage.removeItem(SLUG_KEY);
    } catch { /* noop */ }
  };

  const setAppendOrderId = (slugArg, oid) => {
    setAppendOrderIdState(oid || null);
    try {
      if (oid) localStorage.setItem(APPEND_KEY, JSON.stringify({ slug: slugArg, order_id: oid }));
      else localStorage.removeItem(APPEND_KEY);
    } catch { /* noop */ }
  };

  // ---------- cart mutations ----------
  const add = (item, rid) => {
    if (restaurantId && rid && restaurantId !== rid) {
      setItems([]);
    }
    setRestaurantId(rid);
    setItems((prev) => {
      const found = prev.find((p) => p.item_id === item.id);
      if (found) return prev.map((p) => (p.item_id === item.id ? { ...p, qty: p.qty + 1 } : p));
      return [
        ...prev,
        { item_id: item.id, name: item.name, price: item.price, qty: 1, image_url: item.image_url, notes: "", allergens: item.allergens || [] },
      ];
    });
  };
  const inc = (id) => setItems((p) => p.map((i) => (i.item_id === id ? { ...i, qty: i.qty + 1 } : i)));
  const dec = (id) => setItems((p) => p.flatMap((i) => (i.item_id === id ? (i.qty > 1 ? [{ ...i, qty: i.qty - 1 }] : []) : [i])));
  const remove = (id) => setItems((p) => p.filter((i) => i.item_id !== id));

  const clear = () => {
    setItems([]);
    setRestaurantId(null);
    // Also nuke shared cart on the server if we were in shared mode
    if (slug && tableNo) {
      api.delete(`/customer/restaurant/${slug}/table/${tableNo}/cart`).catch(() => {});
    }
  };

  const total = items.reduce((s, i) => s + i.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <CartCtx.Provider
      value={{
        items,
        restaurantId,
        slug,
        setSlug,
        tableNo,
        setTableNo,
        appendOrderId,
        setAppendOrderId,
        add,
        inc,
        dec,
        remove,
        clear,
        total,
        count,
        sharedActive,
      }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
